/**
 * The user and manager guide. One list feeds both the little "?" bubbles beside each feature
 * and the full guide at /help, so they always say the same thing.
 *
 * Writing rules: short sentences, the exact words on the buttons in quotes, one action per
 * step, no technical words.
 */

export type HelpAudience = 'staff' | 'manager';

export interface HelpSection {
  id: string;
  audience: HelpAudience;
  title: string;
}

export interface HelpTopic {
  id: string;
  section: string;
  title: string;
  /** One or two sentences: what it is for. Shown first in the bubble. */
  short: string;
  /** How to use it, one action per step. */
  steps: string[];
  /** Good to know. Shown in the guide (and the first one in the bubble). */
  tips?: string[];
  /** Where to find it in the app, in words. */
  where?: string;
  /** A button in the guide that opens the feature. */
  open?: { to: string; label: string };
  related?: string[];
}

export const HELP_SECTIONS: HelpSection[] = [
  { id: 'start', audience: 'staff', title: 'Getting started' },
  { id: 'count', audience: 'staff', title: 'The closing count' },
  { id: 'shift', audience: 'staff', title: 'During the shift' },
  { id: 'daily', audience: 'manager', title: 'Every day' },
  { id: 'excel', audience: 'manager', title: 'The Excel files' },
  { id: 'planning', audience: 'manager', title: 'Ordering, trends and specials' },
  { id: 'setup', audience: 'manager', title: 'Setting up' },
];

export const HELP_TOPICS: HelpTopic[] = [
  // ---------------------------------------------------------------- staff: getting started
  {
    id: 'home-screen',
    section: 'start',
    title: 'The home screen',
    short: 'Tonight’s count is on the home screen as four big buttons — one for each part of the count sheet.',
    steps: ['Tap an area to count it: “Back beer”, “Front bar beer”, “Liquor” or “Liquor guns”.', 'Tap “Promo or spillage” when a drink is given away, spilled or broken, and “Brought from downstairs” for bottles and cases brought up.', 'When all four areas say “Done”, tap the green “Finish tonight’s count”.', 'Tap “Home” at the top of any screen to come back here.'],
    tips: ['Tap a round “?” next to anything to see what it does.', 'In Practice mode (the yellow “Practice” at the top), tap “Go to the real counts” on the home screen, or the yellow “Practice” itself, to get back to the real counts.'],
    where: 'The first screen of the app.',
    open: { to: '/', label: 'Go to the home screen' },
    related: ['who-is-using', 'finish-count'],
  },
  {
    id: 'feedback',
    section: 'start',
    title: 'Report a problem or an idea',
    short: 'Something not working, confusing, or could be better? Send a note. It goes to the manager area, with the screen you were on.',
    steps: [
      'On the home screen, tap “Report a problem or an idea” (on a tablet, the speech-bubble button at the top works from any screen).',
      'Pick “Something’s wrong”, “Something’s confusing” or “An idea”.',
      'Type what happened, or what would help. For a problem: what you tapped, and what you expected.',
      'Tap “Send”.',
    ],
    tips: ['Managers read it in the manager area under “Feedback”, and mark each one looked at or done.', 'Your name, the screen and the kind of device go with it, so a glitch can be found again.'],
    related: ['home-screen'],
  },
  {
    id: 'who-is-using',
    section: 'start',
    title: 'Pick your name',
    short: 'The tablet writes your initials on the sheet, so pick your name before you count.',
    steps: ['Tap your name at the top right (or “Not you?” on the home screen).', 'Tap your name in the list.', 'Managers with a PIN type their 4 numbers.'],
    tips: ['Every day after 4 a.m. the app signs out whoever was using it, and asks “Who is using this device?” the next time it’s opened. Tap your own name, so the counts show who really did them.', 'A manager’s name switches back to staff after 15 minutes without a tap.'],
    where: 'Top right of every screen.',
    related: ['home-screen'],
  },

  // ---------------------------------------------------------------- staff: the count
  {
    id: 'count-back-beer',
    section: 'count',
    title: 'Count the back beer',
    short: 'Count every beer in the back, in the same order as the sheet. One number for each: the bottles and cans you see.',
    steps: ['Tap “Back beer” on the home screen.', 'For each beer, type how many there are, or use − and +. Beers with a box size have two boxes: full boxes, and loose singles.', 'If there are none, type 0. Don’t leave it empty.', 'When every beer has a number, tap “Back beer is done”.'],
    tips: ['Numbers save by themselves. You can stop and come back.', 'Empty is not the same as 0: empty means “not counted yet”.'],
    open: { to: '/', label: 'Go to the home screen' },
    related: ['count-front-beer', 'finish-count'],
  },
  {
    id: 'count-front-beer',
    section: 'count',
    title: 'Count the front bar beer',
    short: 'Count the beer in the front bar cooler. If it’s filled up as usual, one tap fills in the usual amounts.',
    steps: ['Tap “Front bar beer” on the home screen.', 'If the cooler was filled up as usual, tap “Fill in usual amounts”.', 'Look at each beer and fix any that are different.', 'Count the ones still empty (they change too much to fill in).', 'Tap “Front bar beer is done”.'],
    tips: ['Each beer shows its usual amount under its name.'],
    related: ['usual-amounts', 'count-back-beer'],
  },
  {
    id: 'usual-amounts',
    section: 'count',
    title: 'Fill in usual amounts',
    short: 'Puts the usual number in for every front bar beer that isn’t counted yet. Beers a manager set to “count every time” are left empty.',
    steps: ['Tap “Fill in usual amounts”.', 'Tap “Fill them in”.', 'Check each beer and change any that are different.'],
    tips: ['It never changes a number you already typed.'],
    related: ['count-front-beer'],
  },
  {
    id: 'count-liquor',
    section: 'count',
    title: 'Count the liquor',
    short: 'Type the ounces for each liquor and liqueur, the same number you would write on the sheet.',
    steps: ['Tap “Liquor” on the home screen.', 'For each bottle, type the ounces (like 23.65).', 'If there is none, type 0.', 'Scroll down for the liqueurs and the safe.', 'Tap “Liquor is done”.'],
    tips: ['Up to two decimals, like on the sheet.'],
    related: ['liquor-guns'],
  },
  {
    id: 'voice-count',
    section: 'count',
    title: 'Count by voice',
    short: 'Say the drink and how many, and it goes in that drink’s box. Typing in the same box works too.',
    steps: [
      'Open an area (Back beer, Front bar beer or Liquor).',
      'Tap “Count by voice” and say the drink, then how many: “Coors Light 24”, “Coors Light 2 boxes and 5”, “Crown Royal 23.6”.',
      'A green line shows what was saved, and that drink’s box lights up. Keep going with the next drink.',
      'Wrong? Say “scratch that” or tap Undo. Tap “Stop listening” when you’re done.',
      'No “Count by voice” button (an iPad app)? Tap the box, tap the microphone on the keyboard, speak, then tap Add.',
    ],
    tips: [
      'Say “none” for zero: “Blue Curacao none”.',
      'Boxes only work for beers with a box size (a manager sets it in Drink lists).',
      'If two drinks sound alike, it asks “Which one?”. Tap the right one.',
      'Speak close to the tablet. In a noisy bar, typing can be quicker.',
      'Chrome turns the voice into text on Google’s servers, so it needs the internet. The app never keeps any audio.',
    ],
    related: ['count-back-beer', 'count-liquor'],
  },
  {
    id: 'liquor-guns',
    section: 'count',
    title: 'Read the liquor guns',
    short: 'Write down the number on each liquor gun’s meter at close.',
    steps: ['Tap “Liquor guns” on the home screen.', 'Type the number on gun 1’s meter.', 'Type the number on gun 2’s meter.', 'Tap “Save both readings”.'],
    tips: ['A meter only goes up. If your number is lower than last night’s, the app asks you to check it.'],
    related: ['count-liquor'],
  },
  {
    id: 'finish-count',
    section: 'count',
    title: 'Finish tonight’s count',
    short: 'When the four areas are done, send the count to a manager.',
    steps: ['Check that all four areas say “Done”.', 'Tap the green “Finish tonight’s count”.', 'That’s it. A manager checks it.'],
    tips: ['After this, only a manager can change numbers — except drinks a manager asks you to count again.'],
    related: ['recounts'],
  },
  {
    id: 'recounts',
    section: 'count',
    title: 'Count something again',
    short: 'A manager can ask for a drink to be counted again. It shows on the home screen in yellow.',
    steps: ['On the home screen, tap the drink under “Please count these again”.', 'Count it again and change the number if it’s different.', 'Tap “Done — I counted it again”, even if the number was right.', 'The app takes you to the next one, or home when they’re all done.'],
    tips: ['Your initials go in the “Recount Initial” row of the sheet.', 'Recounts can be done until 4 p.m. the day after the count. After that one goes away and a manager sorts it out.'],
    related: ['finish-count'],
  },

  // ---------------------------------------------------------------- staff: during the shift
  {
    id: 'promo-spill',
    section: 'shift',
    title: 'Promo or spillage',
    short: 'Write down any drink given away, spilled or broken. It goes in the “Promo/Spillage” row of the sheet.',
    steps: ['Tap “Promo or spillage” on the home screen.', 'Pick what happened: “Promo / given away”, “Spilled”, “Broken” or “Something else”.', 'Pick the drink.', 'Type how many (bottles for beer, ounces for liquor).', 'Add a note if you want (for “Something else” it’s needed), then tap “Save”.'],
    tips: ['Made a mistake? Tap “Take off” next to it and add it again.', 'Bottles and cases brought up go under “Brought from downstairs”.'],
    related: ['count-back-beer'],
  },

  {
    id: 'brought-up',
    section: 'shift',
    title: 'Brought from downstairs',
    short: 'Write down bottles and cases brought up from the basement (or straight from a delivery). It goes in the “Beer Added” and “Liquor Added” rows, so tonight’s count adds up.',
    steps: [
      'Tap “Brought from downstairs” on the home screen.',
      '“Brought from downstairs” is already picked. If it came straight off a delivery truck, tap “From a delivery”.',
      'Type part of the name and tap the drink.',
      'Liquor: how many bottles and what size (26, 40 or 60 oz). Beer: how many, or tap “+ case of 24”.',
      'Check “Who brought it up?” (it starts on your name). If someone else carried it, pick them.',
      'Tap “Save”.',
    ],
    tips: ['Add it when you bring it up, before counting — then the count won’t be flagged.', 'Made a mistake? Tap “Take off” next to it and add it again.', 'Every entry (date, drink, how much, who brought it up) is on the “Brought up” sheet of the weekly Excel file, like the paper log.'],
    open: { to: '/', label: 'Go to the home screen' },
    related: ['promo-spill', 'count-liquor'],
  },

  // ---------------------------------------------------------------- manager: every day
  {
    id: 'manager-area',
    section: 'daily',
    title: 'The manager area',
    short: 'Nights to approve, the weekly Excel file, and everything else for managers.',
    steps: ['Tap “Manager area” on the home screen (managers only).', 'Check “Nights to check” first.', 'Use the menu for everything else.', 'Tap “Staff screen” to go back.'],
    open: { to: '/admin', label: 'Open the manager area' },
    related: ['check-a-night', 'week-excel'],
  },
  {
    id: 'check-a-night',
    section: 'daily',
    title: 'Check and approve a night',
    short: 'Look at the drinks that changed more than usual, type the Micros numbers, and approve the night.',
    steps: ['Open “Check & approve nights” and tap the night.', 'For each drink listed, tap “Looks right” or “Ask for a recount”.', 'Type the Micros numbers and tap “Save the Micros numbers”.', 'Look at the variance for beer and liquor.', 'Tap “Approve the night”.'],
    tips: ['A recount shows on the staff home screen in yellow. This page updates when it’s done.', 'Recounts can be asked for until 4 p.m. the day after the count (an owner can change the time in Settings). After that the stock has changed, so fix the number yourself or tap “Looks right”.', 'Approving marks anything left as “looks right”.'],
    open: { to: '/admin/nights', label: 'Open the nights' },
    related: ['count-changes', 'micros-numbers', 'beer-sheet'],
  },
  {
    id: 'deliveries',
    section: 'daily',
    title: 'Deliveries & transfers',
    short: 'Beer and liquor that came in (the “Beer Added” and “Liquor Added” rows) and beer sent to another bar (“Transfered Out”). Promo and spills can be added here too.',
    steps: ['Open “Deliveries & transfers” (or the night’s page → “Deliveries, transfers & promo”).', 'Pick the night: “Night before” or a date.', 'Pick what happened: “Came in”, “Moved out”, or “Promo, spilled or broken”.', 'Pick the drink, type how many (or use the case and bottle buttons), and tap “Save”.'],
    tips: ['Add a delivery on the night it went on the shelf, so that night’s count adds up.', 'Liquor has no “moved out” row on the sheet.'],
    open: { to: '/admin/moves', label: 'Open Deliveries & transfers' },
    related: ['check-a-night', 'count-changes'],
  },
  {
    id: 'count-changes',
    section: 'daily',
    title: 'Counts that changed more than usual',
    short: 'Each drink is compared with what usually goes out on that night of the week. Big differences, counts that went up, and missing counts are listed for a look.',
    steps: ['Read the numbers: last night, what came in, tonight, and the usual.', 'If it makes sense, tap “Looks right”.', 'If not, tap “Ask for a recount” (add a note for staff if you like). Recounts can be asked for until 4 p.m. the day after the count; after that, fix the number in the count yourself.'],
    tips: ['“Went up” usually means a delivery wasn’t written down in “Deliveries & transfers”.', 'The limits (12 bottles, 10 oz, 50 %) can be changed in Settings.', 'The app only says “requires review” — never more than that.'],
    related: ['check-a-night', 'settings'],
  },
  {
    id: 'micros-numbers',
    section: 'daily',
    title: 'The Micros numbers',
    short: 'The “1 BTL… 1 STAFF BEER” and “1OZ… STAFF LIQUOR” rows, typed from the night’s Micros report. The app doesn’t connect to Micros — nothing to break.',
    steps: ['Print or open the night’s Micros report.', 'In the night’s page, type each number in its box.', 'Leave a box empty if it isn’t on the report.', 'Tap “Save the Micros numbers”.'],
    tips: ['MICROS BEER SOLD = 1 BTL + 2 × 2 BTL + 5 × 5 BTL + coolers + staff beer, like the sheet.'],
    related: ['beer-sheet', 'liquor-sheet'],
  },
  {
    id: 'beer-sheet',
    section: 'daily',
    title: 'The beer numbers and variance',
    short: 'The night’s column of Beer Count Close: opening, added, out, closing, sold, Micros, and the variance — the same formulas as the sheet.',
    steps: ['TOTAL BEER SOLD = opening + added − transferred − promo − closing.', 'Variance = MICROS BEER SOLD − TOTAL BEER SOLD.', 'Negative (red): more went out than Micros rang up — requires review.'],
    tips: ['Opening comes from the night before. If the night before isn’t in the app, type it with “Opening numbers”.'],
    related: ['opening-numbers', 'micros-numbers'],
  },
  {
    id: 'liquor-sheet',
    section: 'daily',
    title: 'The liquor numbers and variance',
    short: 'The night’s column of Closing Liquor: both guns, back bar used, total ounces sold, Micros, and the variance.',
    steps: ['Gun used = tonight’s reading − last night’s.', 'Back Bar Used = opening + added − promo − closing.', 'TOTAL OZ SOLD = gun 1 + gun 2 + back bar.', 'Variance = MICROS OZ SOLD − TOTAL OZ SOLD.'],
    related: ['opening-numbers', 'liquor-guns'],
  },
  {
    id: 'opening-numbers',
    section: 'daily',
    title: 'Opening numbers',
    short: 'Openings normally come from the night before, like the sheet’s formulas. Type them only for the first night in the app, or after a night that wasn’t counted.',
    steps: ['On the night’s page, tap “Opening numbers”.', 'Type the opening beer, liquor, or gun reading that’s missing.', 'Leave the rest empty to use the night before.', 'Tap “Save”.'],
    tips: ['On Mondays these are the yellow cells of the Excel sheet.'],
    related: ['beer-sheet', 'liquor-sheet'],
  },

  // ---------------------------------------------------------------- manager: excel
  {
    id: 'week-excel',
    section: 'excel',
    title: 'Download a week’s Excel file',
    short: 'Every week saves as your count workbook — Closing Liquor, Beer Count Close and Beer Order — with the same rows and formulas.',
    steps: ['Open “Week & Excel file”.', 'Pick the week with “Week before” or a date.', 'On a tablet or phone, tap “Share or email” and pick Mail, Save to Files, OneDrive or Google Drive. On a computer, tap “Download the Excel file”.', 'Make sure it ends up in your email or shared folder.'],
    tips: ['Save last week’s file every Monday. The overview reminds you.', 'The Excel files are the copy kept off the tablet. Keep them.', 'OneDrive or Google Drive only show in the share menu when that app is on the tablet.'],
    open: { to: '/admin/week', label: 'Open Week & Excel file' },
    related: ['excel-files', 'open-excel'],
  },
  {
    id: 'excel-files',
    section: 'excel',
    title: 'Excel files and settings',
    short: 'Open Excel files to bring weeks back in, save every week at once, and change settings.',
    steps: ['Open “Excel files & settings”.', 'To bring weeks back, use “Open an Excel file”.', 'To keep a full copy, tap “Save every week”.'],
    open: { to: '/admin/data', label: 'Open Excel files & settings' },
    related: ['open-excel', 'week-excel'],
  },
  {
    id: 'open-excel',
    section: 'excel',
    title: 'Bring weeks back from Excel',
    short: 'Read a week back in from an Excel file — after a tablet reset, or to load older weeks for trends.',
    steps: ['Open “Excel files & settings”.', 'Under “Open an Excel file”, choose the file.', 'Read what was found.', 'Tap “Bring these numbers in”.'],
    tips: ['New or reset tablet: open the latest week first. It brings back the drink lists, people and settings too.', 'Nights already on the tablet are kept unless you tick “Replace”.', 'Older workbooks typed by hand work too (.xls or .xlsx).'],
    open: { to: '/admin/data', label: 'Open Excel files & settings' },
    related: ['excel-files'],
  },

  // ---------------------------------------------------------------- manager: planning
  {
    id: 'beer-order',
    section: 'planning',
    title: 'Beer order',
    short: 'The Beer Order sheet on screen: par, what’s in the back, and cases to order. Nothing is sent to anyone.',
    steps: ['Open “Beer order”.', 'Read the boxes to order for each beer, and how many bottles under par.', 'Change pars and box sizes in “Drink lists”.'],
    tips: ['Order = (par − in the back) ÷ bottles in a box, the sheet’s formula. It uses the last finished back-beer count.'],
    open: { to: '/admin/order', label: 'Open Beer order' },
    related: ['drink-lists'],
  },
  {
    id: 'trends',
    section: 'planning',
    title: 'Trends noticed',
    short: 'Plain notes about what’s changing — selling more, selling less, busiest night, not moving — each with the numbers and how it was worked out.',
    steps: ['Open “Sales trends”.', 'Read each note.', 'Tap “How we worked this out” for the reasoning.'],
    tips: ['A trend is only noted when the numbers clearly back it up. They start after about four weeks of counts.'],
    open: { to: '/admin/trends', label: 'Open Sales trends' },
    related: ['what-sold', 'what-to-expect'],
  },
  {
    id: 'what-sold',
    section: 'planning',
    title: 'What sold',
    short: 'What went out over any dates you pick, from the counts.',
    steps: ['Open “Sales trends”, then “What sold”.', 'Pick the dates.', 'Pick Beer or Liquor to see the busiest nights.'],
    tips: ['Sales (est.) uses the drink prices in “Drink lists”. It’s from the counts, not Micros.'],
    related: ['trends'],
  },
  {
    id: 'what-to-expect',
    section: 'planning',
    title: 'What to expect',
    short: 'How much each drink should sell over the coming days, with the reasons.',
    steps: ['Open “Sales trends”, then “What to expect”.', 'Pick the days.', 'Add events like Dry January if they apply.'],
    related: ['events', 'trends'],
  },
  {
    id: 'events',
    section: 'planning',
    title: 'Events (Dry January, long weekends)',
    short: 'Days you expect to sell more or less. You set the %; forecasts use it.',
    steps: ['Open “Sales trends”, then “Events”.', 'Tap a suggested date or “Add an event”.', 'Type how much more or less you expect, in %.', 'Tap “Save”.'],
    related: ['what-to-expect'],
  },
  {
    id: 'drink-costs',
    section: 'planning',
    title: 'Drink costs',
    short: 'What each drink costs you and what it makes, before tax.',
    steps: ['Open “Drink costs & specials”.', 'Tap “Change” on a drink to add its cost, bottle size, price and pour.', 'Read the cost, profit and pour cost per drink.'],
    tips: ['Liquor needs the bottle cost and bottle size; beer just the cost of one.'],
    open: { to: '/admin/specials', label: 'Open Drink costs & specials' },
    related: ['plan-special'],
  },
  {
    id: 'plan-special',
    section: 'planning',
    title: 'Plan a special',
    short: 'Compare specials: how many more drinks each needs to break even, and the extra profit at your guess.',
    steps: ['Choose the deal: $ off, % off, or one price.', 'Choose the nights.', 'Optional: type how many more drinks you think it brings, in %.', 'Read the best and easiest specials at the top.'],
    related: ['drink-costs'],
  },

  // ---------------------------------------------------------------- manager: setup
  {
    id: 'drink-lists',
    section: 'setup',
    title: 'Drink lists',
    short: 'The rows of the count sheets, in the same order. Add, rename, reorder or take drinks off.',
    steps: ['Open “Drink lists” and pick a list.', 'Use the arrows to move a drink up or down.', 'Tap a drink to change its name, box size (beer), full bottle size and spout weight (liquor), usual amount, par, cost or price.', 'Add a drink at the bottom.'],
    tips: ['Taking a drink off a list keeps its past counts.', 'Yellow “No box size yet” / “No bottle size yet” shows what still needs setting.', 'Every liquor bottle has Red’s 0.70 oz spout unless changed; the Spouts box at the top of the Liquor tab sets it for every bottle at once.', 'Front bar beer: untick “Count it every time” to give a beer a usual amount.'],
    open: { to: '/admin/items', label: 'Open Drink lists' },
    related: ['usual-amounts', 'beer-order'],
  },
  {
    id: 'people',
    section: 'setup',
    title: 'People and PINs',
    short: 'Everyone who uses the tablet, their initials for the sheet, and PINs for managers.',
    steps: ['Open “People & PINs”.', 'Tap “Add a person”, type the name and initials, pick the role, and Save.', 'For a manager or owner, tap “Set a PIN” next to their name and type 4 numbers twice.', 'Anyone with a PIN can change their own with “Change PIN” on their name.'],
    tips: ['Staff count. Managers approve, use Excel files and add staff. Owners also change settings, make managers and owners, and set anyone’s PIN.'],
    open: { to: '/admin/people', label: 'Open People & PINs' },
    related: ['who-is-using'],
  },
  {
    id: 'settings',
    section: 'setup',
    title: 'Settings',
    short: 'The venue name, when a new night starts, and when a count needs a manager’s look.',
    steps: ['Open “Excel files & settings”.', 'Change the settings.', 'Tap “Save settings”.'],
    tips: ['Only an owner can change settings.'],
    open: { to: '/admin/data', label: 'Open Excel files & settings' },
    related: ['count-changes'],
  },
  {
    id: 'sharing',
    section: 'setup',
    title: 'Sharing between devices',
    short: 'Use a second tablet or a phone too. Every count, recount and approval shows on all of them, and each one keeps working with no internet.',
    steps: [
      'On the first tablet (owner): Excel files & settings → “Start sharing”.',
      'Tap “Add a device”. A join code shows for 15 minutes.',
      'On the new device: open the app → “Red’s Bar and Grill — real counts” → “Join a tablet or phone that’s already set up” → type the code.',
      'Everything comes over. Pick your name and count as usual.',
    ],
    tips: [
      'With no internet a device shows “Not shared yet” and catches up by itself later.',
      'Two people can count different areas at the same time on different devices.',
      'Each device can save the weekly Excel file — they all have the same numbers.',
    ],
    open: { to: '/admin/data', label: 'Open Excel files & settings' },
    related: ['excel-files', 'people'],
  },
  {
    id: 'history',
    section: 'setup',
    title: 'History of changes',
    short: 'Every count, change and approval on this tablet: who, when and what.',
    steps: ['Open “History of changes”.', 'Search for a drink, a name or a date.'],
    open: { to: '/admin/history', label: 'Open History of changes' },
  },
];

export const topicById = new Map(HELP_TOPICS.map((t) => [t.id, t]));
export const sectionById = new Map(HELP_SECTIONS.map((s) => [s.id, s]));

export function audienceOf(t: HelpTopic): HelpAudience {
  return sectionById.get(t.section)!.audience;
}
